import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Check, Play, Trash2, Plus, ExternalLink } from 'lucide-react';
import { Section, Field, ImageUpload } from './_shared';

export default function HeroPanel({ cx }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); try { const r = await cx.get('/settings'); setSettings(r.data || {}); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async (overrides = {}) => {
    try {
      const merged = { ...settings, ...overrides };
      const payload = {
        hero_home_image: merged.hero_home_image || '',
        hero_home_video: merged.hero_home_video || '',
        hero_video_library: merged.hero_video_library || [],
        hero_about_image: merged.hero_about_image || '',
        hero_strategy_image: merged.hero_strategy_image || '',
        hero_security_image: merged.hero_security_image || '',
      };
      const r = await cx.put('/settings', payload);
      setSettings(r.data || merged);
      toast.success('Saved');
    } catch { toast.error('Save failed'); }
  };

  if (loading || !settings) return <Section title="Hero Images & Video"><p className="text-sec text-sm font-mono">Loading…</p></Section>;

  const library = settings.hero_video_library || [];
  const updateSlot = (idx, patch) => {
    const next = library.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setSettings({ ...settings, hero_video_library: next });
  };
  const addSlot = () => {
    if (library.length >= 8) { toast.error('Max 8 slots'); return; }
    setSettings({ ...settings, hero_video_library: [...library, { id: crypto.randomUUID(), label: 'New video', url: '' }] });
  };
  const removeSlot = (idx) => {
    const next = library.filter((_, i) => i !== idx);
    setSettings({ ...settings, hero_video_library: next });
  };
  const applySlot = (slot) => {
    if (!slot.url) { toast.error('Paste a URL into this slot first'); return; }
    save({ hero_home_video: slot.url });
  };

  const activeUrl = settings.hero_home_video || '';

  return (
    <Section title="Hero Images & Video" action={<button onClick={() => save()} data-testid="admin-hero-save" className="btn-primary text-sm"><Save size={14} /> Save all</button>}>
      <div className="space-y-8">
        {/* === HOME VIDEO LIBRARY === */}
        <div className="border border-subtle rounded-md p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-display text-prim text-lg">Home hero · Video library</p>
              <p className="text-xs text-muted mt-1">
                Paste up to 8 MP4/WebM URLs and click <span className="text-brand-teal-light">Use this</span> to hot-swap the home hero video instantly. Find free Qatar drone footage at <a href="https://www.pexels.com/search/videos/doha/" target="_blank" rel="noreferrer" className="text-brand-teal-light hover-line">pexels.com/videos/doha</a> · <a href="https://coverr.co/s/qatar" target="_blank" rel="noreferrer" className="text-brand-teal-light hover-line">coverr.co</a>.
              </p>
            </div>
            <button onClick={addSlot} className="btn-secondary text-xs"><Plus size={12} /> Add slot</button>
          </div>

          <div className="mt-5 space-y-3">
            {library.map((slot, idx) => {
              const isActive = slot.url && slot.url === activeUrl;
              return (
                <div key={slot.id} data-testid={`hero-video-slot-${idx}`} className={`border rounded-md p-4 transition-colors ${isActive ? 'border-brand-teal-light/60 bg-brand-teal/5' : 'border-subtle surface'}`}>
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="font-mono text-[10px] tracking-[0.2em] text-muted">SLOT {String(idx + 1).padStart(2, '0')}</span>
                    {isActive && <span className="font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-brand-teal/15 text-brand-teal-light border border-brand-teal/30 inline-flex items-center gap-1"><Check size={10} /> Active on Home</span>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <input
                      className="lux-input md:col-span-4"
                      placeholder="Label (e.g. Doha Skyline)"
                      value={slot.label}
                      onChange={(e) => updateSlot(idx, { label: e.target.value })}
                      data-testid={`hero-video-label-${idx}`}
                    />
                    <input
                      type="url"
                      className="lux-input md:col-span-8"
                      placeholder="https://… .mp4 / .webm"
                      value={slot.url}
                      onChange={(e) => updateSlot(idx, { url: e.target.value })}
                      data-testid={`hero-video-url-${idx}`}
                    />
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                      onClick={() => applySlot(slot)}
                      disabled={!slot.url || isActive}
                      data-testid={`hero-video-use-${idx}`}
                      className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Play size={12} fill="currentColor" /> {isActive ? 'In use' : 'Use this'}
                    </button>
                    {slot.url && (
                      <a href={slot.url} target="_blank" rel="noreferrer" className="btn-secondary text-xs">
                        <ExternalLink size={12} /> Preview source
                      </a>
                    )}
                    <button onClick={() => removeSlot(idx)} className="text-xs text-brand-maroon-light hover:underline inline-flex items-center gap-1 ml-auto"><Trash2 size={12} /> Remove slot</button>
                  </div>
                </div>
              );
            })}
            {library.length === 0 && <p className="text-muted text-sm">No video slots. Click "Add slot" to start.</p>}
          </div>

          {activeUrl && (
            <div className="mt-5 pt-5 border-t border-subtle">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted mb-2">Currently active home video</p>
              <p className="text-sec text-xs break-all font-mono">{activeUrl}</p>
              <button onClick={() => save({ hero_home_video: '' })} className="mt-2 text-xs text-brand-maroon-light hover:underline">Clear (go back to neural network background)</button>
            </div>
          )}
        </div>

        {/* === OTHER PAGE HEROES === */}
        {[
          { k: 'hero_about_image', label: 'About hero', note: 'Leave empty to use the default image.' },
          { k: 'hero_strategy_image', label: 'Strategy hero', note: 'Leave empty to use the default image.' },
          { k: 'hero_security_image', label: 'Security hero', note: 'Leave empty to use the default image.' },
        ].map((row) => (
          <div key={row.k} className="border border-subtle rounded-md p-4">
            <p className="font-display text-prim mb-3">{row.label}</p>
            <ImageUpload value={settings[row.k]} onChange={(v) => setSettings({ ...settings, [row.k]: v })} label="Upload" />
            <p className="text-xs text-muted mt-2">{row.note}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}
